import { ChangeDetectionStrategy, Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import * as moment from "moment";
import { ImageTile } from "./report-image-edit-form.state";
import { ReportImageOption } from "@core/data/types/advanced-report";

export interface ReportImagePreviewData {
    fileRef: string;
    caption: string;
    tooltip: string | null;
    /** Full identity, so the preview stands alone without the grid's headers for context. */
    context: { label: string, value: string }[];
}

export interface ReportImagePreviewResult {
    select: boolean;
}

/**
 * Caption for a sample-photo tile: the capture date, with the same `~` upload-date convention as the
 * tile scrim (F-8.6). Epic 2's asset tabs supply their own caption (asset name / filename) instead.
 */
export function previewDataFor(tile: ImageTile): ReportImagePreviewData {
    // local time, matching the tile scrim's date pipe: takenAt/createdAt are real timestamps, not the
    // DATE columns (evalStartDate/birthDate) that must be pinned to the server's day
    let parsed = tile.captureDate ? moment(tile.captureDate) : null;
    let date = parsed && parsed.isValid() ? parsed.format('D MMM YYYY') : 'Date unknown';

    let option = tile.option;
    let site = [option.siteBlock, option.siteName].filter(part => !!part).join(' · ');
    let evalDate = option.evalStartDate ? moment.parseZone(option.evalStartDate).format('D MMM YYYY') : null;

    return {
        fileRef: option.fileRef,
        caption: tile.isUploadDate ? `~ ${date}` : date,
        tooltip: tile.isUploadDate ? 'Upload date; capture date unknown.' : null,
        // mirrors the grid's tiers so the preview carries the same identity the headers gave it
        context: [
            { label: 'Cultivar', value: option.cultivarLabel || 'Unknown' },
            { label: 'Site', value: site || 'No site' },
            { label: 'Sample', value: option.sampleShortLabel || option.sampleCode || 'Unnamed' },
            { label: 'Evaluation', value: evalDate || 'Undated' },
        ],
    };
}

/**
 * Caption for a Logos & Graphics tile: the asset's own name (F-8.6) — there's no capture date,
 * grouping, or sample identity to fall back on, since assets aren't wrapped as an ImageTile.
 */
export function previewDataForAsset(option: ReportImageOption): ReportImagePreviewData {
    return {
        fileRef: option.fileRef,
        caption: option.name || 'Untitled image',
        tooltip: null,
        context: [
            { label: 'Name', value: option.name || 'Untitled image' },
        ],
    };
}

/**
 * Lightweight uncropped preview. Deliberately not ImageGalleryDialog: that one needs
 * SampleImage width/height for its download menu and runs a virtual-scroll thumbnail strip,
 * none of which the picker has or wants. Epic 2's asset tabs reuse this with a different caption.
 */
@Component({
    selector: 'pv-report-image-preview-dialog',
    templateUrl: 'report-image-preview.dialog.html',
    styleUrls: ['report-image-preview.dialog.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    host: {
        class: 'pv-report-image-preview-dialog',
    }
})
export class ReportImagePreviewDialog {

    constructor(
        private _dialogRef: MatDialogRef<ReportImagePreviewDialog, ReportImagePreviewResult>,
        @Inject(MAT_DIALOG_DATA) public data: ReportImagePreviewData,
    ) {}

    select() {
        this._dialogRef.close({ select: true });
    }

    close() {
        this._dialogRef.close();
    }
}
